/**
 * The add/edit account form.
 *
 * The three ingest checkboxes are off by default and stay that way: fetching a
 * 90,000-row catalog nobody asked for is the failure mode this product exists
 * to avoid, so a pre-ticked box here would undo the whole design.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import {
  IMPORT_SCOPE_LABEL,
  IPTV_ORG_PLAYLIST_URL,
  normalizePlaylistUrl,
  presetScopeForUrl,
  type AccountSummary,
  type ImportScope,
  type IngestSelection,
  type OutputFormat,
} from '@sflx/shared';
import type { CreateAccountBody } from '../lib/api';
import { ErrorNotice, Spinner } from './Feedback';

export interface AccountFormValues extends CreateAccountBody {}

interface AccountFormProps {
  /** Present when editing; the password field then means "leave unchanged". */
  account?: AccountSummary;
  busy: boolean;
  error: unknown;
  submitLabel: string;
  onSubmit: (values: Partial<AccountFormValues>) => void;
  onCancel?: () => void;
}

export function AccountForm({ account, busy, error, submitLabel, onSubmit, onCancel }: AccountFormProps) {
  const editing = Boolean(account);
  const editingPublic = Boolean(account?.importScope);

  const [label, setLabel] = useState(account?.label ?? '');
  const [serverUrl, setServerUrl] = useState(editingPublic || !editing ? '' : (account?.serverUrl ?? ''));
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [scope, setScope] = useState<ImportScope | null>(account?.importScope ?? (editing ? null : 'us'));
  // What the Playlist URL field holds. A preset fills it in; typing anything else makes it a custom playlist.
  const [playlistUrl, setPlaylistUrl] = useState(account?.importScope ? account.serverUrl : IPTV_ORG_PLAYLIST_URL.us);
  const [localError, setLocalError] = useState<Error | null>(null);
  const [userAgent, setUserAgent] = useState(account?.userAgent ?? '');
  // Not a choice the form offers. A browser cannot play raw MPEG-TS — there is
  // no demuxer for it and the proxy cannot supply one — so offering "MPEG-TS"
  // as a live format only ever produced a failed playback that then blamed the
  // provider for a setting the user had picked here. HLS is preferred whenever
  // the panel allows it, and a TS-only panel falls back to TS on its own, where
  // the message is at least true. The stored value is kept so an account
  // created before this still round-trips.
  const outputFormat: OutputFormat = account?.outputFormat ?? 'hls';
  const [ingest, setIngest] = useState<IngestSelection>(
    account?.ingest ?? { live: false, movies: false, series: false },
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    setLocalError(null);

    const user = username.trim();
    const pass = password.trim();
    const named = label.trim() ? { label: label.trim() } : {};
    const agent = userAgent.trim() ? { userAgent: userAgent.trim() } : {};

    // A protected line being edited, with no public scope picked. An empty
    // password keeps the stored one.
    if (editing && account && !account.importScope && !scope) {
      onSubmit({
        ingest,
        outputFormat,
        ...named,
        ...agent,
        ...(pass ? { password: pass } : {}),
      });
      return;
    }

    if (user && pass) {
      if (!serverUrl.trim()) {
        setLocalError(new Error('A protected playlist needs a server URL.'));
        return;
      }
      onSubmit({
        ingest,
        outputFormat,
        serverUrl: serverUrl.trim(),
        username: user,
        password: pass,
        ...named,
        ...agent,
      });
      return;
    }

    if (user || pass) {
      setLocalError(new Error('Enter both a username and a password, or leave both empty.'));
      return;
    }

    if (scope) {
      const address = playlistUrl.trim();
      const preset = presetScopeForUrl(address);
      if (!preset) {
        try {
          normalizePlaylistUrl(address);
        } catch (caught) {
          setLocalError(caught instanceof Error ? caught : new Error('The playlist URL is not valid'));
          return;
        }
      }
      onSubmit({
        importScope: preset ?? 'custom',
        ...(preset ? {} : { playlistUrl: address }),
        ingest: { live: false, movies: false, series: false },
        outputFormat,
        ...named,
        ...agent,
      });
      return;
    }

    setLocalError(new Error('Choose US Only, All Countries or a custom URL, or enter a username and password.'));
  };

  const chooseScope = (value: ImportScope) => {
    setScope(value);
    if (value !== 'custom') setPlaylistUrl(IPTV_ORG_PLAYLIST_URL[value]);
    // Custom starts from an empty field. Leaving a preset address there would save as that preset.
    else if (presetScopeForUrl(playlistUrl)) setPlaylistUrl('');
  };

  const typePlaylistUrl = (value: string) => {
    setPlaylistUrl(value);
    setScope(presetScopeForUrl(value) ?? 'custom');
  };

  const toggle = (key: keyof IngestSelection) =>
    setIngest((previous) => ({ ...previous, [key]: !previous[key] }));

  const keepingProtected = Boolean(editing && account && !account.importScope && !scope);
  const showPublicUrl = Boolean(scope) && !username.trim() && !password.trim();

  return (
    <form className="card stack" onSubmit={submit}>
      <h2>{editing ? `Edit ${account?.label}` : 'Add playlist'}</h2>

      <div className="field">
        <label htmlFor="acc-label">Name (optional)</label>
        <input
          id="acc-label"
          className="input"
          placeholder="Living room line"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
      </div>

      <fieldset className="field" style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="label">Import scope</legend>
        <div className="stack stack--tight" style={{ marginTop: 'var(--s-2)' }}>
          {editing && account && !account.importScope ? (
            <label className="check">
              <input
                type="radio"
                name="import-scope"
                checked={scope === null}
                onChange={() => setScope(null)}
              />
              <span className="check__text">
                <span>Keep this protected server</span>
                <span className="check__hint">Leave the username and password as they are, or set a new password.</span>
              </span>
            </label>
          ) : null}
          {(['us', 'all', 'custom'] as const).map((value) => (
            <label className="check" key={value}>
              <input
                type="radio"
                name="import-scope"
                checked={scope === value}
                onChange={() => chooseScope(value)}
              />
              <span className="check__text">
                <span>{IMPORT_SCOPE_LABEL[value]}</span>
                <span className="check__hint">
                  {value === 'custom' ? 'Any public .m3u or .m3u8 playlist address.' : IPTV_ORG_PLAYLIST_URL[value]}
                </span>
              </span>
            </label>
          ))}
        </div>
        <span className="check__hint">
          {keepingProtected
            ? 'Choose US Only, All Countries or Custom URL to replace this line with a public playlist. Credentials stay optional.'
            : 'US Only plays the published United States playlist. All Countries plays the full public playlist. Custom URL plays any public playlist you enter below. Leave username and password empty.'}
        </span>
      </fieldset>

      {showPublicUrl && scope ? (
        <div className="field">
          <label htmlFor="acc-playlist-url">Playlist URL</label>
          <input
            id="acc-playlist-url"
            className="input"
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            placeholder="https://example.com/playlist.m3u"
            value={playlistUrl}
            onChange={(event) => typePlaylistUrl(event.target.value)}
          />
        </div>
      ) : (
        <div className="field">
          <label htmlFor="acc-url">Server URL</label>
          <input
            id="acc-url"
            className="input"
            disabled={keepingProtected}
            placeholder="http://panel.example.com:8080"
            value={keepingProtected ? (account?.serverUrl ?? '') : serverUrl}
            onChange={(event) => setServerUrl(event.target.value)}
          />
        </div>
      )}

      <div className="row" style={{ gap: 'var(--s-4)', flexWrap: 'wrap' }}>
        <div className="field" style={{ flex: '1 1 180px' }}>
          <label htmlFor="acc-user">Username (optional)</label>
          {keepingProtected && account ? (
            <input id="acc-user" className="input" disabled value={account.username} />
          ) : (
            <input
              id="acc-user"
              className="input"
              autoComplete="off"
              value={username}
              onChange={(event) => setUsername(event.target.value)}
            />
          )}
        </div>
        <div className="field" style={{ flex: '1 1 180px' }}>
          <label htmlFor="acc-pass">Password (optional)</label>
          <input
            id="acc-pass"
            className="input"
            type="password"
            autoComplete="new-password"
            placeholder={keepingProtected ? 'unchanged' : ''}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
      </div>
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        Leave both empty to play the public playlist for the scope above. Fill in both, plus a server URL, for a
        protected line.
      </p>

      <div className="row" style={{ gap: 'var(--s-4)', flexWrap: 'wrap' }}>
        <div className="field" style={{ flex: '2 1 240px' }}>
          <label htmlFor="acc-ua">User-Agent (optional)</label>
          <input
            id="acc-ua"
            className="input"
            placeholder="VLC/3.0.18 LibVLC/3.0.18"
            value={userAgent}
            onChange={(event) => setUserAgent(event.target.value)}
          />
        </div>
      </div>

      {keepingProtected || (username.trim() && password.trim()) ? (
      <div className="field">
        <span className="label">What to fetch</span>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          Nothing is fetched until you tick a box, and unticking one deletes that catalog again.
        </p>
        <div className="stack stack--tight" style={{ marginTop: 'var(--s-2)' }}>
          <IngestCheck
            checked={ingest.live}
            onChange={() => toggle('live')}
            title="Live TV"
            hint="Channels. You pick which countries after the first fetch."
          />
          <IngestCheck
            checked={ingest.movies}
            onChange={() => toggle('movies')}
            title="Movies"
            hint="Video on demand, with posters and plots."
          />
          <IngestCheck
            checked={ingest.series}
            onChange={() => toggle('series')}
            title="TV Series"
            hint="Seasons and episodes."
          />
        </div>
      </div>
      ) : null}

      <ErrorNotice error={localError ?? error} />

      <div className="row" style={{ justifyContent: 'flex-end' }}>
        {onCancel ? (
          <button type="button" className="btn btn--ghost" onClick={onCancel}>
            Cancel
          </button>
        ) : null}
        <button type="submit" className="btn btn--primary" disabled={busy}>
          {busy ? <Spinner /> : null}
          {submitLabel}
        </button>
      </div>
    </form>
  );
}

function IngestCheck({
  checked,
  onChange,
  title,
  hint,
}: {
  checked: boolean;
  onChange: () => void;
  title: string;
  hint: string;
}) {
  return (
    <label className="check">
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="check__text">
        <span>{title}</span>
        <span className="check__hint">{hint}</span>
      </span>
    </label>
  );
}
